import { Mailbox } from "@/lib/types";
import { deleteMailbox } from "@/services/mailbox";
import { Button } from "@/components/ui/button";
import { Trash2, Users, User, ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type MailboxItemProps = {
  mailbox: Mailbox;
  onDelete: () => void;
  expandedMailboxes: Set<string>;
};

export function MailboxItem({ mailbox, onDelete, expandedMailboxes }: MailboxItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const handleOpenDeleteDialog = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering mailbox expansion
    setIsDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    const success = await deleteMailbox(mailbox.id);
    setIsDeleting(false);
    
    if (success) {
      onDelete();
    }
    setIsDeleteDialogOpen(false);
  };
  
  // Handler to prevent dialog actions from triggering parent click events
  const handleDialogAction = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  
  return (
    <>
      <div className="flex col-span-2">
          {expandedMailboxes.has(mailbox.id) ?
              <ChevronDown className="h-5 w-5 text-muted-foreground mr-1.5 translate-y-[3px]" /> :
              <ChevronRight className="h-5 w-5 text-muted-foreground mr-1.5 translate-y-[3px]" />
          }
          {mailbox.email}
      </div>
      <div className="flex">
        <Badge variant={mailbox.type === 'shared' ? 'secondary' : 'outline'} className="flex gap-1">
          {mailbox.type === 'shared' ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
          {mailbox.type === 'shared' ? 'Shared Mailbox' : 'Standard Mailbox'}
        </Badge>
      </div>
      <div>
        <span 
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            mailbox.status === 'active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {mailbox.status}
        </span>
      </div>
      <div>
        <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
          {mailbox.rules_count || 0}
        </span>
      </div>
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleOpenDeleteDialog}
          disabled={isDeleting}
          className="text-destructive hover:text-destructive hover:bg-destructive hover:text-white"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          {isDeleting ? "Removing..." : "Remove"}
        </Button>
      </div>

      <AlertDialog 
        open={isDeleteDialogOpen} 
        onOpenChange={(open) => {
          // When dialog changes state, prevent event propagation
          setIsDeleteDialogOpen(open);
        }}
      >
        <AlertDialogContent onClick={handleDialogAction}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Mailbox</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the mailbox "{mailbox.email}"? This action cannot be undone.
              {(mailbox.rules_count || 0) > 0 && (
                <span className="block mt-2 text-amber-600">
                  Warning: This will also delete {mailbox.rules_count} rule{(mailbox.rules_count || 0) !== 1 ? 's' : ''} associated with this mailbox.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDialogAction}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                handleDialogAction(e);
                handleConfirmDelete();
              }} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
